import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";

export type AvailabilityStatus =
  | "unknown"
  | "requested"
  | "not_available"
  | "available"
  | "booked";

export interface ProjectAvailability {
  id: string;
  project_id: string;
  person_id: string;
  status: AvailabilityStatus;
  available_days: string[];
  notes: string | null;
  crew_list_remark: string | null;
  unity_username: string | null;
  created_at: string;
  updated_at: string;
  person?: {
    id: string;
    first_name: string;
    last_name: string;
    person_type: string;
    phone: string | null;
    unity_username: string | null;
    email: string | null;
    allergies: string | null;
    allergies_notes: string | null;
    remarks: string | null;
    person_allergy_tags?: {
      allergy_tag: { id: string; name: string };
    }[];
  };
}

export interface FunctionAssignment {
  id: string;
  function_id: string;
  person_id: string;
  remark: string | null;
  created_at: string;
  updated_at: string;
  person?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  /** Virtual driver assignments: the vehicle driven (like a camera badge) */
  vehicle_name?: string | null;
  vehicle_id?: string | null;
}

export interface ProjectFunctionDay {
  id: string;
  project_id: string;
  series_id: string;
  function_date: string;
  required_count_override: number | null;
  created_at: string;
  updated_at: string;
  assignments?: FunctionAssignment[];
}

export type LocationType = "remote" | "on_site" | null;

export interface ProjectFunctionGroup {
  id: string;
  project_id: string;
  name: string;
  location_type: LocationType;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectFunctionSeries {
  id: string;
  project_id: string;
  project_function_group_id: string | null;
  name: string;
  required_count: number;
  selected_days: string[];
  system_tag: string | null;
  location_type: LocationType;
  created_at: string;
  updated_at: string;
  project_function_group?: ProjectFunctionGroup | null;
  days: ProjectFunctionDay[];
  /** Derived from other data (e.g. vehicle bookings), not stored; read-only */
  is_virtual?: boolean;
}

export interface ProjectPersonTravelDay {
  id: string;
  project_id: string;
  person_id: string;
  travel_date: string;
  remark: string | null;
  created_at: string;
  updated_at: string;
}

function sortAssignments(assignments: FunctionAssignment[] | undefined) {
  return [...(assignments ?? [])].sort((a, b) => {
    const aName = `${a.person?.last_name ?? ""} ${a.person?.first_name ?? ""}`;
    const bName = `${b.person?.last_name ?? ""} ${b.person?.first_name ?? ""}`;
    return aName.localeCompare(bName);
  });
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};

type ZeroAvailabilityRow = {
  id: string;
  projectID: string;
  personID: string;
  status: AvailabilityStatus;
  availableDays?: (number | string)[] | null;
  notes?: string | null;
  crewListRemark?: string | null;
  unityUsername?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  person?: {
    id: string;
    firstName: string;
    lastName: string;
    personType: string;
    phone?: string | null;
    unityUsername?: string | null;
    email?: string | null;
    allergies?: string | null;
    allergiesNotes?: string | null;
    remarks?: string | null;
    personAllergyTags?: {
      id: string;
      personID: string;
      allergyTagID: string;
      allergyTag?: {
        id: string;
        name: string;
      } | null;
    }[];
  } | null;
};

type ZeroFunctionAssignmentRow = {
  id: string;
  functionID: string;
  personID: string;
  remark?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  person?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
};

type ZeroProjectFunctionDayRow = {
  id: string;
  projectID: string;
  seriesID?: string | null;
  functionDate: number | string;
  requiredCountOverride?: number | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  assignments?: ZeroFunctionAssignmentRow[] | null;
};

type ZeroProjectFunctionSeriesRow = {
  id: string;
  projectID: string;
  projectFunctionGroupID?: string | null;
  name: string;
  requiredCount: number;
  selectedDays?: (number | string)[] | null;
  systemTag?: string | null;
  locationType?: LocationType;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  projectFunctionGroup?: ZeroProjectFunctionGroupRow | null;
  days?: ZeroProjectFunctionDayRow[] | null;
};

type ZeroProjectFunctionGroupRow = {
  id: string;
  projectID: string;
  name: string;
  locationType?: LocationType;
  displayOrder?: number | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroProjectPersonTravelDayRow = {
  id: string;
  projectID: string;
  personID: string;
  travelDate: number | string;
  remark?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type MutationOptions<T> = { onSuccess?: (data: T) => void };

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyDateString(value: number | string) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value;
}

function toLegacyAvailability(row: ZeroAvailabilityRow): ProjectAvailability {
  return {
    id: row.id,
    project_id: row.projectID,
    person_id: row.personID,
    status: row.status,
    available_days: (row.availableDays ?? []).map(toLegacyDateString),
    notes: row.notes ?? null,
    crew_list_remark: row.crewListRemark ?? null,
    unity_username: row.unityUsername ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    person: row.person
      ? {
          id: row.person.id,
          first_name: row.person.firstName,
          last_name: row.person.lastName,
          person_type: row.person.personType,
          phone: row.person.phone ?? null,
          unity_username: row.person.unityUsername ?? null,
          email: row.person.email ?? null,
          allergies: row.person.allergies ?? null,
          allergies_notes: row.person.allergiesNotes ?? null,
          remarks: row.person.remarks ?? null,
          person_allergy_tags: (row.person.personAllergyTags ?? [])
            .filter((assignment) => assignment.allergyTag)
            .map((assignment) => ({
              allergy_tag: {
                id: assignment.allergyTag!.id,
                name: assignment.allergyTag!.name,
              },
            })),
        }
      : undefined,
  };
}

function toZeroDateStringKey(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return String(Date.UTC(year, month - 1, day));
}

function toLegacyFunctionAssignment(
  row: ZeroFunctionAssignmentRow,
): FunctionAssignment {
  return {
    id: row.id,
    function_id: row.functionID,
    person_id: row.personID,
    remark: row.remark ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    person: row.person
      ? {
          id: row.person.id,
          first_name: row.person.firstName,
          last_name: row.person.lastName,
        }
      : undefined,
  };
}

function toLegacyFunctionDay(row: ZeroProjectFunctionDayRow) {
  return {
    id: row.id,
    project_id: row.projectID,
    series_id: row.seriesID ?? "",
    function_date: toLegacyDateString(row.functionDate),
    required_count_override: row.requiredCountOverride ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    assignments: sortAssignments(
      (row.assignments ?? []).map(toLegacyFunctionAssignment),
    ),
  };
}

function toLegacyFunctionSeries(
  row: ZeroProjectFunctionSeriesRow,
): ProjectFunctionSeries {
  return {
    id: row.id,
    project_id: row.projectID,
    project_function_group_id: row.projectFunctionGroupID ?? null,
    name: row.name,
    required_count: row.requiredCount,
    selected_days: [...(row.selectedDays ?? [])].map(toLegacyDateString).sort(),
    system_tag: row.systemTag ?? null,
    location_type: row.locationType ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    project_function_group: row.projectFunctionGroup
      ? toLegacyProjectFunctionGroup(row.projectFunctionGroup)
      : null,
    days: [...(row.days ?? [])]
      .map(toLegacyFunctionDay)
      .sort((a, b) => a.function_date.localeCompare(b.function_date)),
  };
}

function toLegacyProjectFunctionGroup(
  row: ZeroProjectFunctionGroupRow,
): ProjectFunctionGroup {
  return {
    id: row.id,
    project_id: row.projectID,
    name: row.name,
    location_type: row.locationType ?? null,
    display_order: row.displayOrder ?? 0,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyTravelDay(
  row: ZeroProjectPersonTravelDayRow,
): ProjectPersonTravelDay {
  return {
    id: row.id,
    project_id: row.projectID,
    person_id: row.personID,
    travel_date: toLegacyDateString(row.travelDate),
    remark: row.remark ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useProjectAvailability(projectId: string | undefined) {
  const query = projectId
    ? appQueries.projectAvailability.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const availability = ((data ?? []) as ZeroAvailabilityRow[]).map(
    toLegacyAvailability,
  );

  return {
    data: availability,
    isLoading:
      !!projectId && details.type !== "complete" && availability.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useUpsertAvailability() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (availability: {
    project_id: string;
    person_id: string;
    status: AvailabilityStatus;
    available_days?: string[];
    notes?: string;
  }) => {
    const input = {
      id: generateUUID(),
      projectID: availability.project_id,
      personID: availability.person_id,
      status: availability.status,
      availableDays: availability.available_days ?? [],
      notes: availability.notes ?? null,
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectAvailability.upsert(input),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Availability update was rejected",
      );
      return toLegacyAvailability({
        ...input,
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      availability: {
        project_id: string;
        person_id: string;
        status: AvailabilityStatus;
        available_days?: string[];
        notes?: string;
      },
      options?: MutationOptions<ProjectAvailability>,
    ) => {
      void run(availability)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update availability: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useBatchUpdateAvailability() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    projectId,
    personIds,
    status,
    days,
  }: {
    projectId: string;
    personIds: string[];
    status: AvailabilityStatus;
    days?: string[];
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectAvailability.batchUpsert({
          projectID: projectId,
          personIDs: personIds,
          status,
          availableDays: days ?? [],
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Availability batch update was rejected",
      );
      return { projectId, personIds };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: {
        projectId: string;
        personIds: string[];
        status: AvailabilityStatus;
        days?: string[];
      },
      options?: MutationOptions<{ projectId: string; personIds: string[] }>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success(
            `Updated availability for ${data.personIds.length} people`,
          );
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to batch update: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteAvailability() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({ id, projectId }: { id: string; projectId: string }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectAvailability.delete({ id, projectID: projectId }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Availability delete was rejected",
      );
      return projectId;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: MutationOptions<string>,
    ) => {
      void run(input)
        .then((projectId) => options?.onSuccess?.(projectId))
        .catch((error) =>
          toast.error("Failed to remove person: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

// Update crew list remark on availability
export function useUpdateAvailabilityRemark() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    crewListRemark,
    projectId,
  }: {
    id: string;
    crewListRemark: string | null;
    projectId: string;
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectAvailability.updateRemark({
          id,
          projectID: projectId,
          crewListRemark,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Availability remark update was rejected",
      );
      return projectId;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: {
        id: string;
        crewListRemark: string | null;
        projectId: string;
      },
      options?: MutationOptions<string>,
    ) => {
      void run(input)
        .then((projectId) => options?.onSuccess?.(projectId))
        .catch((error) =>
          toast.error("Failed to update remark: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateAvailabilityUnityUsername() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    unityUsername,
    projectId,
  }: {
    id: string;
    unityUsername: string | null;
    projectId: string;
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectAvailability.updateUnityUsername({
          id,
          projectID: projectId,
          unityUsername,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Availability Unity username update was rejected",
      );
      return projectId;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: {
        id: string;
        unityUsername: string | null;
        projectId: string;
      },
      options?: MutationOptions<string>,
    ) => {
      void run(input)
        .then((projectId) => options?.onSuccess?.(projectId))
        .catch((error) =>
          toast.error("Failed to update Unity username: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useProjectFunctions(projectId: string | undefined) {
  const query = projectId
    ? appQueries.projectFunctionSeries.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const functions = ((data ?? []) as ZeroProjectFunctionSeriesRow[])
    .map(toLegacyFunctionSeries)
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    data: functions,
    isLoading:
      !!projectId && details.type !== "complete" && functions.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useProjectFunctionGroups(projectId: string | undefined) {
  const query = projectId
    ? appQueries.projectFunctionGroups.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const groups = ((data ?? []) as ZeroProjectFunctionGroupRow[])
    .map(toLegacyProjectFunctionGroup)
    .sort(
      (a, b) =>
        a.display_order - b.display_order || a.name.localeCompare(b.name),
    );

  return {
    data: groups,
    isLoading:
      !!projectId && details.type !== "complete" && groups.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useProjectPersonTravelDays(projectId: string | undefined) {
  const query = projectId
    ? appQueries.projectPersonTravelDays.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const travelDays = ((data ?? []) as ZeroProjectPersonTravelDayRow[]).map(
    toLegacyTravelDay,
  );

  return {
    data: travelDays,
    isLoading:
      !!projectId && details.type !== "complete" && travelDays.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateProjectFunctionGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (group: {
    project_id: string;
    name: string;
    location_type?: LocationType;
    display_order?: number;
  }) => {
    const input = {
      id: generateUUID(),
      projectID: group.project_id,
      name: group.name,
      locationType: group.location_type ?? null,
      displayOrder: group.display_order ?? 0,
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectFunctionGroups.create(input),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function group create was rejected",
      );
      return toLegacyProjectFunctionGroup({
        ...input,
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateProjectFunctionGroup() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    projectId,
    updates,
  }: {
    id: string;
    projectId: string;
    updates: {
      name?: string;
      location_type?: LocationType;
      display_order?: number;
    };
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectFunctionGroups.update({
          id,
          projectID: projectId,
          name: updates.name,
          locationType: updates.location_type,
          displayOrder: updates.display_order,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function group update was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync: run, isPending };
}

export function useUpsertProjectPersonTravelDay() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (travelDay: {
    project_id: string;
    person_id: string;
    travel_date: string;
    remark?: string | null;
  }) => {
    const input = {
      id: generateUUID(),
      projectID: travelDay.project_id,
      personID: travelDay.person_id,
      travelDate: travelDay.travel_date,
      remark: travelDay.remark ?? null,
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectPersonTravelDays.upsert(input),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Travel day update was rejected",
      );
      return toLegacyTravelDay({
        ...input,
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      travelDay: Parameters<typeof run>[0],
      options?: MutationOptions<ProjectPersonTravelDay>,
    ) => {
      void run(travelDay)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update travel day: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteProjectPersonTravelDay() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({ id, projectId }: { id: string; projectId: string }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectPersonTravelDays.delete({
          id,
          projectID: projectId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Travel day delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: Parameters<typeof run>[0]) => {
      void run(input).catch((error) =>
        toast.error("Failed to remove travel day: " + error.message),
      );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useCreateProjectFunctionSeries() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (series: {
    project_id: string;
    project_function_group_id?: string | null;
    name: string;
    required_count?: number;
    selected_days: string[];
    system_tag?: string | null;
    location_type?: LocationType;
  }) => {
    const id = generateUUID();
    const selectedDays = [...new Set(series.selected_days)].sort();
    const dayIDsByDate = Object.fromEntries(
      selectedDays.map((day) => [toZeroDateStringKey(day), generateUUID()]),
    );
    const input = {
      id,
      projectID: series.project_id,
      projectFunctionGroupID: series.project_function_group_id ?? null,
      name: series.name,
      requiredCount: series.required_count ?? 1,
      selectedDays,
      systemTag: series.system_tag ?? null,
      locationType: series.location_type ?? null,
      dayIDsByDate,
    };

    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectFunctionSeries.create(input),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function create was rejected",
      );
      return {
        id,
        project_id: series.project_id,
        project_function_group_id: series.project_function_group_id ?? null,
        name: series.name,
        required_count: input.requiredCount,
        selected_days: selectedDays,
        system_tag: series.system_tag ?? null,
        location_type: series.location_type ?? null,
        project_function_group: null,
        created_at: "",
        updated_at: "",
        days: selectedDays.map((day) => ({
          id: dayIDsByDate[toZeroDateStringKey(day)],
          project_id: series.project_id,
          series_id: id,
          function_date: day,
          required_count_override: null,
          created_at: "",
          updated_at: "",
          assignments: [],
        })),
      } satisfies ProjectFunctionSeries;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      series: {
        project_id: string;
        project_function_group_id?: string | null;
        name: string;
        required_count?: number;
        selected_days: string[];
        system_tag?: string | null;
        location_type?: LocationType;
      },
      options?: MutationOptions<ProjectFunctionSeries>,
    ) => {
      void run(series)
        .then((data) => {
          toast.success("Function added");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to add function: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateProjectFunctionSeries() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    projectId,
    updates,
  }: {
    id: string;
    projectId: string;
    updates: {
      name?: string;
      project_function_group_id?: string | null;
      required_count?: number;
      selected_days?: string[];
      system_tag?: string | null;
      location_type?: LocationType;
    };
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectFunctionSeries.update({
          id,
          projectID: projectId,
          projectFunctionGroupID: updates.project_function_group_id,
          name: updates.name,
          requiredCount: updates.required_count,
          selectedDays: updates.selected_days,
          systemTag: updates.system_tag,
          locationType: updates.location_type,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function update was rejected",
      );
      return { projectId };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: Parameters<typeof run>[0],
      options?: MutationOptions<{ projectId: string }>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update function: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteProjectFunctionSeries() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({ id, projectId }: { id: string; projectId: string }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectFunctionSeries.delete({ id, projectID: projectId }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function delete was rejected",
      );
      return projectId;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: MutationOptions<string>,
    ) => {
      void run(input)
        .then((projectId) => {
          toast.success("Function removed");
          options?.onSuccess?.(projectId);
        })
        .catch((error) =>
          toast.error("Failed to remove function: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateProjectFunctionDay() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    projectId,
    requiredCountOverride,
  }: {
    id: string;
    projectId: string;
    requiredCountOverride: number | null;
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectFunctionSeries.updateDay({
          id,
          projectID: projectId,
          requiredCountOverride,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function day update was rejected",
      );
      return { projectId };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: Parameters<typeof run>[0],
      options?: MutationOptions<{ projectId: string }>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update day override: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useCreateFunctionAssignment() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    functionId,
    personId,
    projectId,
    remark,
  }: {
    functionId: string;
    personId: string;
    projectId: string;
    remark?: string;
  }) => {
    const id = generateUUID();
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.functionAssignments.create({
          id,
          functionID: functionId,
          personID: personId,
          projectID: projectId,
          remark: remark || null,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function assignment create was rejected",
      );
      return {
        data: {
          id,
          function_id: functionId,
          person_id: personId,
          remark: remark || null,
          created_at: "",
          updated_at: "",
        } satisfies FunctionAssignment,
        projectId,
      };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: Parameters<typeof run>[0],
      options?: MutationOptions<{
        data: FunctionAssignment;
        projectId: string;
      }>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to assign person: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useBulkAssignFunctionSeries() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    projectId,
    personId,
    functionIds,
  }: {
    projectId: string;
    personId: string;
    functionIds: string[];
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.functionAssignments.bulkAssignSeries({
          projectID: projectId,
          personID: personId,
          functionIDs: functionIds,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Bulk function assignment was rejected",
      );
      return { projectId, inserted: functionIds.length };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: Parameters<typeof run>[0],
      options?: MutationOptions<{ projectId: string; inserted: number }>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success(
            data.inserted > 0
              ? `Assigned person to ${data.inserted} day${
                  data.inserted === 1 ? "" : "s"
                }`
              : "Person already assigned to all selected days",
          );
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to bulk assign person: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateFunctionAssignment() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    remark,
    projectId,
  }: {
    id: string;
    remark: string | null;
    projectId: string;
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.functionAssignments.update({
          id,
          projectID: projectId,
          remark,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function assignment update was rejected",
      );
      return { projectId };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: Parameters<typeof run>[0],
      options?: MutationOptions<{ projectId: string }>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update assignment: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useInitializeProjectFunctions() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    projectId,
    functions,
  }: {
    projectId: string;
    functions: {
      name: string;
      project_function_group_id?: string | null;
      required_count: number;
      selected_days: string[];
      system_tag: string | null;
      location_type?: LocationType;
      day_overrides: { date: string; count: number }[];
    }[];
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projectFunctionSeries.initialize({
          projectID: projectId,
          functions: functions.map((fn) => ({
            id: generateUUID(),
            projectFunctionGroupID: fn.project_function_group_id ?? null,
            name: fn.name,
            requiredCount: fn.required_count,
            selectedDays: fn.selected_days,
            systemTag: fn.system_tag,
            locationType: fn.location_type,
            dayOverrides: fn.day_overrides,
          })),
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Project functions initialize was rejected",
      );
      return { projectId };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: Parameters<typeof run>[0],
      options?: MutationOptions<{ projectId: string }>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Project functions initialized");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to initialize functions: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteFunctionAssignment() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({ id, projectId }: { id: string; projectId: string }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.functionAssignments.delete({ id, projectID: projectId }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Function assignment delete was rejected",
      );
      return projectId;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: MutationOptions<string>,
    ) => {
      void run(input)
        .then((projectId) => options?.onSuccess?.(projectId))
        .catch((error) =>
          toast.error("Failed to unassign person: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
